// @flow
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import * as React from "react";

import BaseField, { type Props as FieldProps } from "./BaseField";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

type Props = {
  ...FieldProps,
  id?: string,
  name?: string,
  onChange: (value: null | [[Date, Date], [string, string]]) => void,
  format?:
    | "MM/DD/YYYY"
    | "DD/MM/YYYY"
    | "YYYY/MM/DD"
    | "YYYY/DD/MM"
    | "MM/YYYY"
    | "YYYY"
    | "Q/YYYY"
    | "MM/DD/YYYY HH:mm"
    | "DD/MM/YYYY HH:mm"
    | "YYYY/MM/DD HH:mm"
    | "YYYY/DD/MM HH:mm"
    | "MM/YYYY HH:mm"
    | "YYYY HH:mm"
    | "Q/YYYY HH:mm",
  size?: "small" | "middle" | "large",
  showTime?: boolean,
  showPastPresets?: boolean,
  showFuturePresets?: boolean,
  defaultValue?: [null | Date | string, null | Date | string],
  value?: [null | Date | string, null | Date | string],
  picker?: null | "week" | "month" | "quarter" | "year",
  disabled?: boolean,
  utcValue?: boolean,
  valueParserFormat?: "YYYY-MM-DDTHH:mm:ss",
  showPastOnly?: boolean,
  showFutureOnly?: boolean,
};

function DateRangeField({
  id,
  name,
  onChange,
  format = "MM/DD/YYYY",
  size = "large",
  showTime,
  showPastPresets,
  showFuturePresets,
  defaultValue,
  value,
  picker,
  disabled,
  utcValue,
  valueParserFormat = "YYYY-MM-DDTHH:mm:ss",
  showPastOnly,
  showFutureOnly,
  ...rest
}: Props): React.Node {
  /*
   * format: https://day.js.org/docs/en/display/format
   */
  const pastPresets = React.useMemo(
    () => [
      { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
      { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
      { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
      { label: "Last 90 Days", value: [dayjs().add(-90, "d"), dayjs()] },
    ],
    [],
  );

  const futurePresets = React.useMemo(
    () => [
      { label: "Next 7 Days", value: [dayjs(), dayjs().add(7, "d")] },
      { label: "Next 14 Days", value: [dayjs(), dayjs().add(14, "d")] },
      { label: "Next 30 Days", value: [dayjs(), dayjs().add(30, "d")] },
      { label: "Next 90 Days", value: [dayjs(), dayjs().add(90, "d")] },
    ],
    [],
  );

  const formatValue = React.useMemo(
    (value) => {
      if (format === "MM/DD/YYYY") {
        let _format = format;
        if (picker === "month") {
          _format = "MM/YYYY";
        } else if (picker === "year") {
          _format = "YYYY";
        } else if (picker === "quarter") {
          _format = "Q/YYYY";
        }

        if (showTime) {
          _format += " HH:mm";
        }

        return _format;
      }

      return format;
    },
    [format, picker, showTime],
  );

  const valueIsString = React.useMemo(() => {
    if (!value) {
      return false;
    }
    return typeof value[0] === "string" || typeof value[1] === "string";
  }, [value]);

  const minDate = React.useMemo(() => {
    if (showFutureOnly) {
      if (showTime) {
        return dayjs().add(1, "m");
      }
      if (picker === "month") {
        return dayjs().add(1, "M");
      }
      return dayjs().add(1, "d");
    }
    return undefined;
  }, [picker, showFutureOnly, showTime]);

  const maxDate = React.useMemo(() => {
    if (showPastOnly) {
      if (showTime) {
        return dayjs().add(-1, "m");
      }
      if (picker === "month") {
        return dayjs().add(-1, "M");
      }
      return dayjs().add(-1, "d");
    }
    return undefined;
  }, [picker, showPastOnly, showTime]);

  const disabledTime = React.useCallback(
    (_, type) => {
      if (showTime) {
        const minTime = minDate ? minDate.toDate() : undefined;
        const maxTime = maxDate ? maxDate.toDate() : undefined;
        const disabledHours = [];
        const disabledMinutes = [];

        if (minTime) {
          for (let i = 0; i < minTime.getHours(); i++) {
            disabledHours.push(i);
          }
          for (let i = 0; i < minTime.getMinutes(); i++) {
            disabledMinutes.push(i);
          }
        }

        if (maxTime) {
          for (let i = 23; i > maxTime.getHours(); i--) {
            disabledHours.push(i);
          }
          for (let i = 59; i > maxTime.getMinutes(); i--) {
            disabledMinutes.push(i);
          }
        }

        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        };
      }
      return undefined;
    },
    [maxDate, minDate, showTime],
  );

  return (
    <BaseField {...rest}>
      <DatePicker.RangePicker
        className="form-input focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] border w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 px-2 mt-1"
        defaultValue={[
          defaultValue
            ? dayjs(
                defaultValue[0],
                valueIsString ? valueParserFormat : undefined,
              )
            : undefined,
          defaultValue
            ? dayjs(
                defaultValue[1],
                valueIsString ? valueParserFormat : undefined,
              )
            : undefined,
        ]}
        disabled={disabled}
        disabledTime={disabledTime}
        format={
          formatValue
            ? {
                format: formatValue,
                type: "mask",
              }
            : undefined
        }
        id={id || name}
        maxDate={maxDate}
        minDate={minDate}
        name={name ?? "date_range_field"}
        onChange={(dates, datesString) => {
          onChange(
            dates
              ? [
                  dates.map((date) =>
                    utcValue ? date.utc().toDate() : date.toDate(),
                  ),
                  datesString,
                ]
              : null,
          );
        }}
        picker={picker ?? undefined}
        presets={
          showPastPresets
            ? pastPresets
            : showFuturePresets
              ? futurePresets
              : undefined
        }
        showTime={showTime ? { format: "HH:mm" } : undefined}
        style={{ paddingTop: "0.438rem", paddingBottom: "0.438rem" }}
        value={
          value
            ? [
                value[0]
                  ? dayjs(
                      value[0],
                      valueIsString ? valueParserFormat : undefined,
                    )
                  : undefined,
                value[1]
                  ? dayjs(
                      value[1],
                      valueIsString ? valueParserFormat : undefined,
                    )
                  : undefined,
              ]
            : undefined
        }
      />
    </BaseField>
  );
}

export default DateRangeField;
